"use client";
import React from "react";
import { motion } from "framer-motion";
import { Caveat } from "next/font/google";
import Image from "next/image";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

export function EnvWebsiteBooking({ section }) {
  return (
    <section className="py-20 relative overflow-visible bg-transparent w-full font-sans">


      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">

        {/* Heading Block: icon + text side by side, left aligned */}
        <div className="flex items-start gap-5 mb-14 max-w-[900px]">

          {/* Calendar Icon - left, vertically aligned with heading */}
          <div className="relative w-[60px] md:w-[80px] h-[80px] flex-shrink-0 mt-2 select-none pointer-events-none hidden md:block">
            <motion.div
              initial={{ opacity: 0, scale: 0.6, rotate: -20 }}
              whileInView={{ opacity: 1, scale: 1, rotate: -10 }}
              viewport={{ once: true }}
              transition={{ type: "spring", stiffness: 180, delay: 0.1 }}
              className="w-full h-full relative"
            >
              <Image
                src={section.calendarIcon}
                alt=""
                fill
                style={{ filter: "hue-rotate(-185deg) saturate(2) brightness(1.1) drop-shadow(0 4px 12px rgba(241,131,59,0.4))" }}
                className="object-contain"
              />
            </motion.div>
          </div>

          {/* Heading - all in Caveat font */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className={`${caveat.className} text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight`}
          >
            {section.headingBefore}{" "}
            <br className="hidden lg:block" />
            <span className="relative inline-block">
              <span className="relative z-10">{section.headingHighlight}</span>
              {/* Brand orange wavy scribble underline */}
              <svg
                className="absolute -bottom-2 left-0 w-full h-4 overflow-visible opacity-95"
                viewBox="0 0 100 10"
                preserveAspectRatio="none"
              >
                <path
                  d="M 2 8 C 25 4, 60 3, 95 6 C 70 8, 30 9, 5 8 C 28 6, 62 5, 92 7"
                  stroke="#f1833b"
                  strokeWidth="3"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
            </span>{" "}
            {section.headingAfter}
          </motion.h2>
        </div>

        {/* Two-Column Content: Image Left + Text/Image Right */}
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">

          {/* Left Column: Image first, then text */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col gap-6"
          >
            <div className="p-1.5 bg-white border border-gray-200 rounded-[14px] shadow-2xl hover:scale-[1.01] transition-transform duration-300 relative">
              <Image
                src={section.image1}
                width={600}
                height={400}
                alt="Appointment booking website"
                className="w-full h-auto rounded-[10px] object-cover"
              />
            </div>
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-gray-300 leading-relaxed text-xl"
              dangerouslySetInnerHTML={{ __html: section.text1 }}
            />
          </motion.div>

          {/* Right Column: Text first, then image */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="flex flex-col gap-6"
          >
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-gray-300 leading-relaxed text-xl"
              dangerouslySetInnerHTML={{ __html: section.text2 }}
            />
            <div className="p-1.5 bg-white border border-gray-200 rounded-[14px] shadow-2xl hover:scale-[1.01] transition-transform duration-300 relative">
              <Image
                src={section.image2}
                width={600}
                height={400}
                alt="Website with appointment booking"
                className="w-full h-auto rounded-[10px] object-cover"
              />
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

export function EnvCrmDashboard({ section }) {
  return (
    <section className="py-20 bg-transparent w-full font-sans">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px]">

        {/* Title */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
          className={`${caveat.className} text-5xl md:text-7xl lg:text-[80px] font-bold text-white text-center leading-tight mb-16`}
        >
          {section.titleBefore}{" "}
          <span className="relative inline-block">
            <span className="relative z-10">{section.titleHighlight}</span>
            {/* Brand green scribble underline - using #10b981 for green since it's a "green highlight" in reference */}
            <svg
              className="absolute -bottom-2 left-0 w-full h-4 overflow-visible opacity-90"
              viewBox="0 0 100 10"
              preserveAspectRatio="none"
            >
              <path
                d="M 2 8 C 25 4, 60 3, 95 6 C 70 8, 30 9, 5 8 C 28 6, 62 5, 92 7"
                stroke="#f48428"
                strokeWidth="3.5"
                fill="none"
                strokeLinecap="round"
              />
            </svg>
          </span>{" "}
          {section.titleAfter}
        </motion.h2>

        {/* Three Columns Paragraphs */}
        <div className="grid md:grid-cols-3 gap-8 lg:gap-16 mb-20">
          {section.paragraphs.map((para, idx) => (
            <motion.p
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              className="text-gray-300 leading-relaxed text-xl"
              dangerouslySetInnerHTML={{ __html: para }}
            />
          ))}
        </div>

        {/* Dashboard Image Section */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative bg-white/5 rounded-[32px] p-8 lg:p-16 flex flex-col items-center mt-10"
        >
          {/* Floating thumbs-up icon */}
          <div className="absolute top-0 left-0 -ml-4 -mt-10 lg:ml-12 lg:-mt-12 w-[60px] md:w-[80px] h-[80px]">
            <motion.div
              initial={{ opacity: 0, scale: 0, rotate: -30 }}
              whileInView={{ opacity: 1, scale: 1, rotate: -10 }}
              viewport={{ once: true }}
              transition={{ type: "spring", stiffness: 200, delay: 0.3 }}
              className="w-full h-full relative"
            >
              <Image
                src={section.thumbsUpIcon}
                alt="Thumbs up"
                fill
                style={{ filter: "hue-rotate(-135deg) saturate(1.5) brightness(1.1) drop-shadow(0 4px 12px rgba(244,132,40,0.4))" }}
                className="select-none pointer-events-none object-contain"
              />
            </motion.div>
          </div>

          {/* Floating Down Arrow */}
          <div className="relative w-[30px] md:w-[35px] h-[35px] mb-6 opacity-90">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ type: "spring", stiffness: 200, delay: 0.4 }}
              className="w-full h-full relative"
            >
              <Image
                src={section.arrowIcon}
                alt="Arrow down"
                fill
                style={{ filter: "brightness(0) saturate(100%) invert(58%) sepia(82%) saturate(2375%) hue-rotate(346deg) brightness(101%) contrast(94%) drop-shadow(0 4px 12px rgba(244,132,40,0.4))" }}
                className="select-none pointer-events-none object-contain"
              />
            </motion.div>
          </div>

          {/* CRM Image */}
          <div className="w-full max-w-[1000px] bg-white rounded-xl shadow-2xl overflow-hidden p-2 transform hover:scale-[1.01] transition-transform duration-500 relative">
            <Image
              src={section.dashboardImage}
              alt="CRM Dashboard"
              width={1000}
              height={600}
              className="w-full h-auto rounded-lg"
            />
          </div>
        </motion.div>

      </div>
    </section>
  );
}

export function EnvProjectManagement({ section }) {
  return (
    <section className="py-20 bg-transparent w-full font-sans">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px]">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">

          {/* Left Column: Images Collage */}
          <div className="relative order-2 lg:order-1 flex justify-center pb-10">
            {/* Background Blob/Circle */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] max-w-[600px] aspect-square bg-white/5 rounded-full -z-10"></div>

            {/* Spark Icon */}
            <div className="absolute -top-10 -left-6 lg:left-0 w-[60px] md:w-[90px] h-[90px] z-20">
              <motion.div
                initial={{ opacity: 0, scale: 0, rotate: -45 }}
                whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
                viewport={{ once: true }}
                transition={{ type: "spring", stiffness: 180, delay: 0.1 }}
                className="w-full h-full relative"
              >
                <Image
                  src={section.sparkIcon}
                  alt=""
                  fill
                  className="pointer-events-none object-contain"
                  style={{ filter: "hue-rotate(345deg) saturate(2) brightness(1.2)" }}
                />
              </motion.div>
            </div>

            {/* Main Image (Project 1) */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative z-10 w-[75%] lg:w-[60%] mr-auto rounded-xl shadow-2xl overflow-hidden p-2 bg-white border border-gray-200"
            >
              <Image src={section.image1} width={400} height={300} alt="Project Management Main" className="w-full h-auto rounded-lg" />
            </motion.div>

            {/* Overlapping Image (Project 2) */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="absolute bottom-0 right-0 lg:right-10 w-[80%] lg:w-[65%] translate-y-10 z-20 rounded-xl shadow-2xl overflow-hidden p-2 bg-white border border-gray-200"
            >
              <Image src={section.image2} width={450} height={320} alt="Project Management Secondary" className="w-full h-auto rounded-lg" />
            </motion.div>
          </div>

          {/* Right Column: Text */}
          <div className="order-1 lg:order-2">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55 }}
              className={`${caveat.className} text-6xl md:text-7xl lg:text-[80px] font-bold text-white leading-tight mb-10`}
            >
              {section.titleBefore}{" "}
              <span className="relative inline-block">
                <span className="relative z-10">{section.titleHighlight}</span>
                {/* Brand Orange Highlight */}
                <svg
                  className="absolute -bottom-2 left-0 w-full h-5 overflow-visible opacity-90"
                  viewBox="0 0 100 10"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M 2 8 C 25 4, 60 3, 95 6 C 70 8, 30 9, 5 8 C 28 6, 62 5, 92 7"
                    stroke="#f48428"
                    strokeWidth="3.5"
                    fill="none"
                    strokeLinecap="round"
                  />
                </svg>
              </span>{" "}
              {section.titleAfter}
            </motion.h2>

            <div className="space-y-6 lg:space-y-8">
              {section.paragraphs.map((para, idx) => (
                <motion.p
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="text-gray-300 leading-relaxed text-xl"
                  dangerouslySetInnerHTML={{ __html: para }}
                />
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}

export function EnvIntegratedInvoicing({ section }) {
  return (
    <section className="py-20 bg-transparent w-full font-sans">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px]">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">

          {/* Left Column: Text */}
          <div className="relative z-10 pb-10 lg:pb-0">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55 }}
              className={`${caveat.className} text-6xl md:text-7xl lg:text-[80px] font-bold text-white leading-tight mb-10`}
            >
              {section.titleBefore}{" "}
              <span className="relative inline-block">
                <span className="relative z-10">{section.titleHighlight}</span>
                {/* Dotted Blue Highlight */}
                <svg
                  className="absolute -bottom-1 left-0 w-full h-4 overflow-visible opacity-90"
                  viewBox="0 0 100 10"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M 2 8 C 25 8, 75 8, 98 8"
                    stroke="#f48428"
                    strokeWidth="4"
                    strokeDasharray="0 15"
                    fill="none"
                    strokeLinecap="round"
                  />
                </svg>
              </span>{" "}
              {section.titleAfter}
            </motion.h2>

            <div className="space-y-6 lg:space-y-8 mb-10">
              {section.paragraphs.map((para, idx) => (
                <motion.p
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="text-gray-300 leading-relaxed text-xl"
                  dangerouslySetInnerHTML={{ __html: para }}
                />
              ))}
            </div>

            {/* Curvy arrow */}
            <div className="hidden lg:block ml-auto mr-10 w-[50px] md:w-[80px] h-[80px]">
              <motion.div
                initial={{ opacity: 0, x: -20, rotate: 250 }}
                whileInView={{ opacity: 1, x: 0, rotate: 270 }}
                viewport={{ once: true }}
                transition={{ type: "spring", stiffness: 100, delay: 0.4 }}
                className="w-full h-full relative"
              >
                <Image
                  src={section.arrowIcon}
                  alt=""
                  fill
                  style={{ filter: "brightness(0) saturate(100%) invert(58%) sepia(82%) saturate(2375%) hue-rotate(346deg) brightness(101%) contrast(94%) drop-shadow(0 4px 12px rgba(244,132,40,0.4))" }}
                  className="opacity-90 pointer-events-none object-contain"
                />
              </motion.div>
            </div>
          </div>

          {/* Right Column: Invoice Image */}
          <div className="relative flex justify-center">

            {/* Invoice Document */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative z-10 w-full max-w-[400px] bg-white rounded-xl shadow-2xl p-2 border border-gray-200"
            >
              <Image src={section.mainImage} width={400} height={550} alt="Integrated Invoicing" className="w-full h-auto rounded-lg" />
            </motion.div>

            {/* Floating Invoice Icon */}
            <div className="absolute top-1/2 right-0 translate-x-1/2 lg:translate-x-1/4 w-[60px] md:w-[80px] h-[80px] z-20">
              <motion.div
                initial={{ opacity: 0, scale: 0, rotate: -30 }}
                whileInView={{ opacity: 1, scale: 1, rotate: 20 }}
                viewport={{ once: true }}
                transition={{ type: "spring", stiffness: 180, delay: 0.3 }}
                className="w-full h-full relative"
              >
                <Image
                  src={section.invoiceIcon}
                  alt=""
                  fill
                  style={{ filter: "hue-rotate(-175deg) saturate(1.5) brightness(1.2)" }}
                  className="pointer-events-none object-contain"
                />
              </motion.div>
            </div>

          </div>

        </div>
      </div>
    </section>
  );
}
